import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Mail, Lock, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const { login, loginWithGoogle, isAuthenticated, isAdmin, user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.organization_id) {
        navigate(isAdmin ? '/admin' : '/department', { replace: true });
      } else {
        navigate('/setup', { replace: true });
      }
    }
  }, [isAuthenticated, isAdmin, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      // Check if 2FA is required
      if (result.requires_2fa) {
        setRequires2FA(true);
        setTwoFAUserId(result.user_id);
        toast.info('Please enter your 2FA code');
        setLoading(false);
        return;
      }
      
      toast.success('Welcome back!');
      // Navigate directly based on role
      if (result.user?.role === 'ADMIN') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/department', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/login/complete-2fa', {
        user_id: twoFAUserId,
        code: twoFACode
      });
      
      // Set session cookie
      await api.post('/auth/set-session-cookie', {
        token: response.data.access_token
      });
      
      updateUser(response.data.user);
      toast.success('Welcome back!');
      
      // Navigate based on role
      if (response.data.user?.role === 'ADMIN') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/department';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setRequires2FA(false);
    setTwoFAUserId(null);
    setTwoFACode('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/20">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 font-['Outfit']">FinOrchest</span>
          </Link>

          {requires2FA ? (
            /* 2FA Verification Form */
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">2FA Verification</h1>
                  <p className="text-gray-500 text-sm">Enter the code from your authenticator app</p>
                </div>
              </div>

              <form onSubmit={handle2FASubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="2fa-code" className="text-gray-600">Authentication Code</Label>
                  <Input
                    id="2fa-code"
                    type="text"
                    inputMode="numeric"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="h-14 text-center text-2xl tracking-[0.5em] bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 placeholder:text-base placeholder:tracking-normal focus:border-blue-600 focus:ring-blue-600"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="2fa-code-input"
                  />
                  <p className="text-xs text-gray-500">You can also use a backup code</p>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-medium shadow-lg shadow-blue-900/20"
                  disabled={loading || twoFACode.length < 6}
                  data-testid="2fa-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifying...</>
                  ) : (
                    'Verify'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-gray-500"
                  onClick={resetToLogin}
                >
                  ← Back to login
                </Button>
              </form>
            </>
          ) : (
            /* Normal Login Form */
            <>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 font-['Outfit']">Welcome back</h1>
              <p className="text-gray-500 mb-8">Sign in to your account to continue</p>

              {/* Google OAuth */}
              <Button
                variant="outline"
                className="w-full mb-6 h-12 border-gray-300 text-gray-700 hover:bg-gray-50 gap-2"
                onClick={loginWithGoogle}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-gray-500">or continue with email</span>
                </div>
              </div>

              {/* Email/Password Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-600">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-10 h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-600">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-blue-600"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-medium shadow-lg shadow-blue-900/20"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Signing in...</>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <p className="text-center text-gray-500 mt-6">
                Don't have an account?{' '}
                <Link to="/register" className="text-blue-700 hover:text-blue-600 font-medium">
                  Sign up
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 primary-glow opacity-50" />
        <div className="relative z-10 text-center px-12">
          <div className="text-6xl font-bold text-blue-900/20 mb-4 font-['Outfit']">FinOrchest</div>
          <p className="text-gray-500 text-lg">Decentralized Fund Management System</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
